import React from 'react';
import { styled } from '@this/constants/themes';
import type { Moment } from 'moment';

import type SelectStore from '@this/domain/select_store';
import type SearchQuery from '@this/domain/search_query';
import OrderItem from '@this/domain/order_item';
import NonOrderItem from '@this/domain/non_order_item';
import type TransportElement from '@this/domain/transport_element';
import HotelElement from '@this/domain/hotel_element';
import type SearchResultItem from '@this/domain/search_result_item';
import type SelectRepository from '@this/domain/select_repository';
import SearchTripForm from '@this/shared/search/search_trip_form';
import { useAITTheme } from '@this/src/components/shared/ui/theme/provider';
import TripsTrainIcon from '@this/shared/icons/trips_train_icon';
import TripsCarIcon from '@this/shared/icons/trips_car_icon';
import TripsPlaneIcon from '@this/shared/icons/trips_plane_icon';
import TripsHotelIcon from '@this/shared/icons/trips_hotel_icon';
import SelectOutlineItem from '../../../reserve_trip/select/outline_item/outline_item';
import OutlineItemShortDistance from '../../../reserve_trip/select/outline_item/OutlineItemShortDistance';
import SelectListArea from '../../../reserve_trip/select/list_area/list_area';
import ListAreaShortDistance from '../../../reserve_trip/select/list_area/short_distance/ListAreaShortDistance';
import TransportBlockTemplate from '../transport_block/transport_block.template';
import HotelBlockTemplate from '../hotel_block/hotel_block.template';
import OrderItemBlock from '../order_item_block/order_item_block';
import TripItemFormTemplate from '../trip_item_form.template';

export interface AirlineName {
  code: string;
  name: string;
}

interface Props {
  serviceId: number | undefined;
  user: any;
  trip: any;
  query: SearchQuery | undefined;
  store: SelectStore | undefined;
  repository: SelectRepository | undefined;
  domesticAirlines: AirlineName[];
  internationalAirlines: AirlineName[];
  addedItems: any;
  editingNonOrderItem: NonOrderItem | null;
  creatingNonOrderItem: NonOrderItem | null;
  selectedItemType: string | null;
  selectItemType: (elementType: string) => void;
  deselectItemType: (e: React.MouseEvent<HTMLElement>) => void;
  startEditingNonOrderItem: (item: NonOrderItem) => void;
  toggleNonOrderItemNeed: (item: NonOrderItem) => void;
  handleSelectItem: () => void;
  handleOpenShowSearchBox: () => void;
  handleShowItemForm: () => void;
  toggleShowSearchBox: () => void;
  changeSmartSearch: () => void;
  handleSmartSearch: (query: SearchQuery) => void;
  addNonOrderItemToTrip: (e: React.FormEvent<HTMLFormElement>) => void;
  handleTimeChange: (item: NonOrderItem, type: 'from' | 'to', time: Moment) => void;
  tripItemFormTemplateErrMsg: string;
  isSmartSearching: boolean;
  showItemForm: boolean;
  showSearchBox: boolean;
  shortdistance: boolean;
  useSmartSearch: boolean;
}

const TripsFormItemformTemplate: React.FC<Props> = ({
  serviceId,
  user,
  trip,
  query,
  store,
  repository,
  domesticAirlines,
  internationalAirlines,
  addedItems,
  editingNonOrderItem,
  creatingNonOrderItem,
  selectedItemType,
  selectItemType,
  deselectItemType,
  startEditingNonOrderItem,
  toggleNonOrderItemNeed,
  handleSelectItem,
  handleOpenShowSearchBox,
  handleShowItemForm,
  toggleShowSearchBox,
  changeSmartSearch,
  handleSmartSearch,
  addNonOrderItemToTrip,
  handleTimeChange,
  tripItemFormTemplateErrMsg,
  isSmartSearching,
  showItemForm,
  showSearchBox,
  shortdistance,
  useSmartSearch
}) => {
  const { theme } = useAITTheme();
  const { colors } = theme.tokens;
  return (
    <>
      {useSmartSearch && (
        <ItemsFormHead>
          <ItemForm visible={showItemForm && (!store || store.currentTab === 'none')}>
            <div className="trip_form_toggle">
              <div className="trip_form_toggle_button">
                <input
                  id="trip_form_toggle"
                  className="trip_form_toggle_input"
                  type="checkbox"
                  checked={isSmartSearching}
                  onClick={changeSmartSearch}
                />
                <label htmlFor="trip_form_toggle" className="trip_form_toggle_label" />
              </div>
              <p>AI Travel Smart Search モード</p>
            </div>
          </ItemForm>
        </ItemsFormHead>
      )}
      <ItemsFormBody>
        <ItemsFormLeft>
          <Outline>
            {addedItems.length === 0 && <ItemsFormStart>1</ItemsFormStart>}
            {addedItems.length > 0 ? (
              addedItems.map((item: NonOrderItem | OrderItem | SearchResultItem, i: number) => (
                <ItemsFormItemWrap key={i}>
                  {item instanceof NonOrderItem ? (
                    <>
                      {item.elements[0] instanceof HotelElement ? (
                        <HotelBlockTemplate
                          icon={i + 1}
                          item={item}
                          element={item.elements[0] as HotelElement}
                          editing={item === editingNonOrderItem}
                          editable={trip === null || trip.draft || trip.tripDetailEditable()}
                          deselectItemType={deselectItemType}
                          startEditingNonOrderItem={startEditingNonOrderItem}
                          toggleNonOrderItemNeed={toggleNonOrderItemNeed}
                        />
                      ) : (
                        <TransportBlockTemplate
                          icon={i + 1}
                          item={item}
                          element={item.elements[0] as TransportElement}
                          editing={item === editingNonOrderItem}
                          editable={trip === null || trip.draft || trip.tripDetailEditable()}
                          deselectItemType={deselectItemType}
                          startEditingNonOrderItem={startEditingNonOrderItem}
                          toggleNonOrderItemNeed={toggleNonOrderItemNeed}
                        />
                      )}
                    </>
                  ) : item instanceof OrderItem ? (
                    <OrderItemBlock item={item} icon={i + 1} />
                  ) : store && handleSelectItem ? (
                    <>
                      {shortdistance ? (
                        <OutlineItemShortDistance
                          key={item.index}
                          sectionNumber={i + 1}
                          item={item}
                          handleClickTab={store.handleClickTab}
                          handleSelectItem={handleSelectItem}
                          selected={store.currentTab === item.index}
                          hotelPriceLimit={item.hotelPriceLimit}
                          store={store}
                          isRequestForm={false}
                          isTripsForm
                          handleOpenShowSearchBox={handleOpenShowSearchBox}
                        />
                      ) : (
                        <SelectOutlineItem
                          key={item.index}
                          icon={i + 1}
                          item={item}
                          handleClickTab={store.handleClickTab}
                          handleSelectItem={handleSelectItem}
                          selected={store.currentTab === item.index}
                          hotelPriceLimit={item.hotelPriceLimit}
                          store={store}
                          direction=""
                          isRequestForm={false}
                          isTripsForm
                          handleOpenShowSearchBox={handleOpenShowSearchBox}
                        />
                      )}
                    </>
                  ) : null}
                </ItemsFormItemWrap>
              ))
            ) : addedItems.length === 0 ? (
              <>
                <ItemsFormItemWrap key={1} />
                <div
                  style={{
                    backgroundColor: colors.brand.background,
                    border: `solid 2px ${colors.brand.primary}`,
                    padding: '15px',
                    fontSize: '14px'
                  }}
                >
                  <div style={{ fontWeight: 'bold', color: colors.brand.primary }}>1つ目の旅程</div>
                  登録する項目を右記から選択してください。
                </div>
              </>
            ) : null}
            {(trip === null || trip.draft || trip.tripDetailEditable()) && addedItems.length > 0 && (
              <ItemsFormInstructionNext>
                <div style={{ fontWeight: 'bold', color: colors.brand.primary }}>
                  {addedItems.length + 1}つ目の旅程
                </div>
                <ItemsFormInstructionButton onClick={() => handleShowItemForm()}>
                  旅程を追加する
                </ItemsFormInstructionButton>
              </ItemsFormInstructionNext>
            )}
            <ItemsFormGoal>+</ItemsFormGoal>
          </Outline>
        </ItemsFormLeft>
        <ItemsFormRight>
          {serviceId &&
            query &&
            repository &&
            store &&
            store.result.items.map((item, i) => (
              <>
                {shortdistance ? (
                  <ListAreaShortDistance
                    key={item.index}
                    hotelPriceLimit={item.hotelPriceLimit}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'separate' && store.currentTab === item.index}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                  />
                ) : (
                  <SelectListArea
                    key={item.index}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'separate' && store.currentTab === item.index}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                  />
                )}
              </>
            ))}
          <ItemForm visible={showItemForm && (!store || store.currentTab === 'none')}>
            <SearchBox>
              {useSmartSearch && isSmartSearching ? (
                <div>
                  <SearchTripForm
                    query={query}
                    user={user}
                    defaultSeatClasses={user ? user.defaultSeatClasses : []}
                    handleSearch={handleSmartSearch}
                  />
                </div>
              ) : trip === null || trip.draft || trip.tripDetailEditable() ? (
                <div style={{ backgroundColor: colors.brand.background, padding: '20px' }}>
                  <ItemTypeListGroup>
                    <TripsTrainIcon />
                    <ItemTypeButton
                      onClick={() => selectItemType('railway_ticket')}
                      style={{
                        backgroundColor: selectedItemType === 'railway_ticket' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'railway_ticket' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      在来線
                    </ItemTypeButton>
                    <ItemTypeButton
                      onClick={() => selectItemType('bus')}
                      style={{
                        backgroundColor: selectedItemType === 'bus' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'bus' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      バス
                    </ItemTypeButton>
                    <ItemTypeButton
                      onClick={() => selectItemType('shinkansen')}
                      style={{
                        backgroundColor: selectedItemType === 'shinkansen' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'shinkansen' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      新幹線
                    </ItemTypeButton>
                  </ItemTypeListGroup>
                  <ItemTypeListGroup>
                    <TripsCarIcon />
                    <ItemTypeButton
                      onClick={() => selectItemType('taxi')}
                      style={{
                        backgroundColor: selectedItemType === 'taxi' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'taxi' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      タクシー
                    </ItemTypeButton>
                    <ItemTypeButton
                      onClick={() => selectItemType('rental_car')}
                      style={{
                        backgroundColor: selectedItemType === 'rental_car' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'rental_car' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      レンタカー
                    </ItemTypeButton>
                  </ItemTypeListGroup>
                  <ItemTypeListGroup>
                    <TripsPlaneIcon />
                    <ItemTypeButton
                      onClick={() => selectItemType('domestic_air')}
                      style={{
                        backgroundColor: selectedItemType === 'domestic_air' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'domestic_air' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      国内航空券
                    </ItemTypeButton>
                    <ItemTypeButton
                      onClick={() => selectItemType('foreign_air')}
                      style={{
                        backgroundColor: selectedItemType === 'foreign_air' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'foreign_air' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      海外航空券
                    </ItemTypeButton>
                  </ItemTypeListGroup>
                  <ItemTypeListGroup>
                    <TripsHotelIcon />
                    <ItemTypeButton
                      onClick={() => selectItemType('hotel')}
                      style={{
                        backgroundColor: selectedItemType === 'hotel' ? colors.brand.primary : '#ffffff',
                        color: selectedItemType === 'hotel' ? '#ffffff' : colors.brand.primary
                      }}
                    >
                      ホテル
                    </ItemTypeButton>
                  </ItemTypeListGroup>
                </div>
              ) : null}
              {!isSmartSearching && selectedItemType && creatingNonOrderItem && (
                <TripItemFormTemplate
                  item={creatingNonOrderItem}
                  itemType={selectedItemType}
                  editing={editingNonOrderItem && editingNonOrderItem.id === creatingNonOrderItem.id}
                  domesticAirlines={domesticAirlines}
                  internationalAirlines={internationalAirlines}
                  deselectItemType={deselectItemType}
                  addNonOrderItemToTrip={addNonOrderItemToTrip}
                  handleTimeChange={handleTimeChange}
                  tripItemFormTemplateErrMsg={tripItemFormTemplateErrMsg}
                />
              )}
            </SearchBox>
          </ItemForm>
        </ItemsFormRight>
      </ItemsFormBody>
    </>
  );
};

const ItemsFormHead = styled.div`
  padding: 0px 20px;
  text-align: right;

  @media screen and (min-width: 768px) {
    margin-bottom: -15px;
  }
`;

const ItemsFormBody = styled.div`
  display: flex;
  padding: 0px 20px 100px;

  @media screen and (max-width: 767px) {
    display: block;
  }
`;

const ItemsFormLeft = styled.div`
  @media screen and (min-width: 768px) {
    width: 50%;
  }
`;

const ItemsFormRight = styled.div`
  padding-top: 15px;

  @media screen and (min-width: 768px) {
    width: 50%;
  }
`;

const Outline = styled.div`
  position: relative;
  margin: 25px 20px 25px 8px;
  border-left: solid 4px ${props => props.theme.iconColor};
  padding: 0 0 10px 10px;

  @media screen and (max-width: 767px) {
    margin-right: 0;
  }
`;

const ItemsFormCircle = styled.div`
  position: absolute;
  left: -12px;
  width: 20px;
  min-width: 20px;
  height: 20px;
  background: ${props => props.theme.iconColor};
  border-radius: 10px;
  color: white;
  line-height: 1;
  font-weight: bold;
  text-align: center;
  font-size: 13px;
  padding: 3px 0;
`;

const ItemsFormStart = styled(ItemsFormCircle)`
  top: -20px;
`;

const ItemsFormGoal = styled(ItemsFormCircle)`
  bottom: -20px;
`;

const ItemsFormItemWrap = styled.div`
  margin-left: -22px;
`;

const ItemsFormInstructionNext = styled.div`
  bottom: 30px;
  left: 30px;
  background-color: #ffffff;
  border: solid 2px ${props => props.theme.accentColor};
  padding: 15px;
  font-size: 14px;
  width: 100%;
`;

const ItemsFormInstructionButton = styled.div`
  padding: 10px;
  border: solid 2px ${props => props.theme.accentColor};
  font-size: 14px;
  text-align: center;
  border-radius: 10px;
  cursor: pointer;
  background-color: ${props => props.theme.accentColor};
  color: #ffffff;
`;

const ItemForm = styled.div<{ visible: boolean }>`
  display: none;

  ${props =>
    props.visible &&
    `
    display: block;
  `}
`;

const SearchBox = styled.div`
  border: solid 1px #ccc;
`;

const ItemTypeListGroup = styled.div`
  display: flex;
  flex-flow: wrap;
  justify-content: start;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const ItemTypeButton = styled.div`
  padding: 10px 5px;
  border: solid 1px ${props => props.theme.accentColor};
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  width: 120px;
  border-radius: 7px;
  color: ${props => props.theme.accentColor};
  background-color: #ffffff;

  &:first-child {
    border-top-width: 2px;
  }

  @media screen and (max-width: 767px) {
    width: 100px;
  }
`;

export default TripsFormItemformTemplate;
